import {
  IPermissionAssignmentFieldsFragment,
  IPermissionFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import { IExpandableTableItem } from '../../../components/ExpandableTable/types';
import React, { useCallback, useMemo } from 'react';
import { permissionActionName, permissionDictionary } from '../utils';
import styled from 'styled-components';
import { uniqBy } from 'lodash';
import { assertNotNil } from '../../../utils/types';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import {
  IBasicSchema,
  IDisplayedPermissionItem,
  PermissionListType,
} from '../types';
import useFeatureFilteredPermissionList from './useFeatureFilteredPermissionList';
import { theme } from '../../../styles/theme';

interface IProps {
  permissionAssignments: IPermissionAssignmentFieldsFragment[];
  schemaFilter: IBasicSchema;
}

interface IPermissionWithSchemas extends IPermissionFieldsFragment {
  selectedSchemas: IBasicSchema[];
}
const PermissionContainer = styled.div`
  padding: 12px 16px;
`;

const ActionContainer = styled.div`
  :not(:last-child) {
    margin-bottom: 12px;
  }
`;
const ActionName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
  margin-bottom: 8px;
`;
const PermissionItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: solid 1px ${theme.colors.border};
  background-color: ${theme.colors.level1};
  div:nth-child(even) {
    background-color: ${theme.colors.level2};
  }
  div:not(:last-child) {
    border-bottom: solid 1px ${theme.colors.border};
  }
`;
const PermissionItem = styled.div`
  font-size: 12px;
  padding: 4px 8px;
  display: flex;
  column-gap: 4px;
  > span {
    width: 300px;
  }
`;

/**
 * this hook is going to use PermissionAssignments to generate a list of expandable table for permission overview page.
 * The expandable table will have the following structure in displayedPermissionList and fit ExpandableTable Component.
 * The diff part compare to usePermissionEditableList: not editable, only display permission for current user.
 * @param permissionAssignments contains all permission for current user.
 * @param schemaFilter only display permission for this schema.
 */
const usePermissionOverviewList = ({
  permissionAssignments,
  schemaFilter,
}: IProps) => {
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);

  const getDisplayedPermissionItems = useCallback(
    (
      permissionProps: IDisplayedPermissionItem,
      currentPermissions: IPermissionWithSchemas[],
    ) => {
      return (
        <PermissionContainer key={permissionProps.title}>
          {permissionProps.items.map((permissionProps, index) => {
            const currentActionWithPermissions = currentPermissions.filter(
              (permission) =>
                permission.action === permissionProps.actionKey &&
                permissionProps.permissionKeys.includes(permission.key),
            );

            if (currentActionWithPermissions.length > 0) {
              return (
                <ActionContainer key={'permission-props-' + index}>
                  <ActionName>
                    {permissionActionName[permissionProps.actionKey]}
                  </ActionName>
                  <PermissionItemContainer>
                    {permissionProps.permissionKeys.map((permissionKey) => {
                      const userPermissions =
                        currentActionWithPermissions.filter(
                          (permission) => permission.key === permissionKey,
                        );
                      if (userPermissions.length === 0) {
                        return null;
                      }

                      // one permissionKey may match multiple permissionAssignments from diff roles.
                      // in this case, they may have diff selected schemas. we need to merge and print them out.
                      const allSchemasFromFoundPermissions =
                        userPermissions.flatMap(
                          (userPermission) => userPermission.selectedSchemas,
                        );
                      const isSelectedAllSchemas =
                        allSchemasFromFoundPermissions.some(
                          (schema) =>
                            schema.id === permissionConfig.allSchema.id,
                        );
                      // if all schemas are selected, we only need to display all schemas
                      const uniqueSchemas = isSelectedAllSchemas
                        ? [permissionConfig.allSchema]
                        : uniqBy(allSchemasFromFoundPermissions, 'id');
                      return (
                        <PermissionItem key={permissionKey}>
                          <span>{permissionDictionary[permissionKey]}</span>
                          <span>
                            {uniqueSchemas
                              .map((schema) => schema.name)
                              .join(', ')}
                          </span>
                        </PermissionItem>
                      );
                    })}
                  </PermissionItemContainer>
                </ActionContainer>
              );
            } else {
              return null;
            }
          })}
        </PermissionContainer>
      );
    },
    [permissionConfig.allSchema],
  );
  const featureFilteredPermissionList = useFeatureFilteredPermissionList();
  const displayedTableList: IExpandableTableItem[] = useMemo(() => {
    if (permissionAssignments.length === 0) {
      return [];
    }

    const permissionList: IPermissionWithSchemas[] = permissionAssignments
      .filter((permissionAssignment) => {
        const { schemas } = permissionAssignment;
        if (schemaFilter.id === permissionConfig.allSchema.id) {
          return true;
        }
        return schemas.some(
          (schema) =>
            schema.id === permissionConfig.allSchema.id ||
            schema.id === schemaFilter.id,
        );
      })
      .map((permissionAssigment) => {
        const { schemas, permission } = permissionAssigment;
        return {
          ...permission,
          selectedSchemas: schemas,
        };
      });

    // need to find out all permission types that have been assigned to the user
    // for example: 2 levels category - Cohort or 1 level Report.
    const permissionOnUser = featureFilteredPermissionList.filter(
      (permissionProps) => {
        return permissionList.some((permission) => {
          if (permissionProps.type === PermissionListType.Items) {
            const permissionKeysInCurrFeature = permissionProps.items.flatMap(
              (permissionProp) => permissionProp.permissionKeys,
            );
            return permissionKeysInCurrFeature.includes(permission.key);
          } else {
            return permissionProps.list.some((permissionItem) => {
              const permissionKeysInCurrFeature = permissionItem.items.flatMap(
                (permissionProp) => permissionProp.permissionKeys,
              );
              return permissionKeysInCurrFeature.includes(permission.key);
            });
          }
        });
      },
    );
    // displaying all permission under the user permission list.
    // generating the expandable table list.
    return permissionOnUser.map((permissionProps) => {
      // if found permission in user's permission list, we can display it's title and contents.
      // this permission list may contains diff actions: read, create, update, delete, in selected entity types.
      const currentPermissions = permissionList.filter((permission) => {
        let currUserPermissionKeys = [];
        if (permissionProps.type === PermissionListType.Category) {
          currUserPermissionKeys = permissionProps.list.flatMap(
            (permissionItem) =>
              permissionItem.items.flatMap(
                (permissionProp) => permissionProp.permissionKeys,
              ),
          );
        } else {
          currUserPermissionKeys = permissionProps.items.flatMap(
            (permissionProp) => permissionProp.permissionKeys,
          );
        }
        return currUserPermissionKeys.includes(permission.key);
      });
      // filter out the permissions not in user's permission list.
      // therefore, current selected permission is existed.
      assertNotNil(currentPermissions);
      if (permissionProps.type === PermissionListType.Items) {
        return {
          title: permissionProps.title,
          type: PermissionListType.Items,
          childElement: getDisplayedPermissionItems(
            permissionProps,
            currentPermissions,
          ),
        };
      } else {
        return {
          title: permissionProps.title,
          type: PermissionListType.Category,
          childElements: permissionProps.list
            // filter off entire sub-list if no permission in the list is in user's permission list.
            // for example Cohort - report is a sublist.
            .filter((getDisplayedPermissionItems) => {
              return getDisplayedPermissionItems.items.some((item) =>
                currentPermissions.some((permission) =>
                  item.permissionKeys.includes(permission.key),
                ),
              );
            })
            .map((displayedPermissionPropsItems) => {
              return {
                title: displayedPermissionPropsItems.title,
                type: PermissionListType.Items,
                childElement: getDisplayedPermissionItems(
                  displayedPermissionPropsItems,
                  currentPermissions,
                ),
              };
            }),
        };
      }
    });
  }, [
    getDisplayedPermissionItems,
    permissionAssignments,
    schemaFilter,
    permissionConfig,
    featureFilteredPermissionList,
  ]);

  return { displayedTableList };
};

export default usePermissionOverviewList;
